"use client"

import * as React from "react"
import { XIcon } from "lucide-react"
import { Dialog as DialogPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

/**
 * Right slide-over dialog. Unlike the centered Dialog, the panel hugs the
 * right edge of the viewport and slides in horizontally. The panel and
 * overlay sit above the sticky site header (z-[60] in the nav template)
 * so the open chat is never overlapped by the header bar. On mobile the
 * panel stops above the fixed bottom nav (h-16, lg:hidden) instead of
 * covering the whole screen.
 */
function ChatSheet({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="chat-sheet" {...props} />
}

function ChatSheetTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="chat-sheet-trigger" {...props} />
}

function ChatSheetClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="chat-sheet-close" {...props} />
}

function ChatSheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="chat-sheet-overlay"
      className={cn(
        "fixed inset-0 z-[70] bg-ui-bg-overlay/50 backdrop-blur-[2px] data-[state=closed]:animate-chat-fade-out data-[state=open]:animate-chat-fade-in",
        className
      )}
      {...props}
    />
  )
}

function ChatSheetContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
}) {
  return (
    <DialogPrimitive.Portal data-slot="chat-sheet-portal">
      <ChatSheetOverlay />
      <DialogPrimitive.Content
        data-slot="chat-sheet-content"
        className={cn(
          "fixed top-0 right-0 bottom-16 z-[70] flex w-full max-w-[880px] flex-col gap-0 overflow-hidden rounded-2xl border border-ui-border-base bg-background shadow-2xl outline-none lg:bottom-0 lg:rounded-none lg:border-l lg:border-r-0 lg:border-y-0",
          "duration-300 data-[state=closed]:animate-chat-slide-out data-[state=open]:animate-chat-slide-in",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="chat-sheet-close"
            className="absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none"
          >
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

function ChatSheetHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="chat-sheet-header"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  )
}

function ChatSheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="chat-sheet-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  )
}

function ChatSheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="chat-sheet-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  ChatSheet,
  ChatSheetClose,
  ChatSheetContent,
  ChatSheetDescription,
  ChatSheetHeader,
  ChatSheetOverlay,
  ChatSheetTitle,
  ChatSheetTrigger,
}
